import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Typography, Box, CircularProgress, Alert, Button } from '@mui/material';
import services from '../../services/services';
import styles from './EmailDownloadPage.module.scss';

const EmailDownloadPage = () => {
    const { fileId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [debugInfo, setDebugInfo] = useState('');

    useEffect(() => {
        
        // Check if user is authenticated
        const token = sessionStorage.getItem('ags_token');
        const userInfo = sessionStorage.getItem('user_info');
        const userRole = sessionStorage.getItem('user_role');
        
        setDebugInfo(`URL: ${window.location.href}, FileId: ${fileId}, Token: ${!!token}`);
        
        if (!token) {
            console.log('No token found, redirecting to login...');
            const returnUrl = `/download/${fileId}`;
            console.log('Return URL will be:', returnUrl);
            navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
            return;
        }

        console.log('User is authenticated, starting download...');
        setDebugInfo(prev => prev + ' | Authenticated: YES');
        
        // Add a small delay to ensure page is fully loaded
        setTimeout(() => {
            handleDownload();
        }, 500);
        
    }, [fileId, navigate, location]);

    const handleDownload = async () => {
        
        if (!fileId) {
            console.error('No fileId provided');
            setError('No file ID provided');
            return;
        }
        
        setDownloading(true);
        setError('');
        setDebugInfo(prev => prev + ' | Download: STARTING');
        
        try {
            console.log('Calling services.downloadFileReq...');
            console.log('Parameters:', {
                id: fileId,
                filename: fileId,
                type: "xlsx"  
            });
            
            const result = await services.downloadFileReq(fileId, fileId, "xlsx");
            console.log('Download result:', result);
            
            setSuccess(true);
            setDownloading(false);
            setDebugInfo(prev => prev + ' | Download: SUCCESS');
            console.log('Download completed successfully');
            
        } catch (error) {
            
            setError(`Download failed: ${error.message || 'Unknown error'}`);
            setDownloading(false);
            setDebugInfo(prev => prev + ` | Download: FAILED - ${error.message}`);
        }
    };

    const handleRetry = () => {
        console.log('Retrying download...');
        handleDownload();
    };

    const handleGoHome = () => {
        navigate('/');
    };

    const handleTestDownload = () => {
        console.log('Testing download with manual trigger...');
        handleDownload();
    };

    // Show current state for debugging
    console.log('=== Current Component State ===');
    console.log('downloading:', downloading);
    console.log('error:', error);
    console.log('success:', success);
    console.log('fileId:', fileId);

    if (downloading) {
        return (
            <Box 
                display="flex" 
                flexDirection="column" 
                alignItems="center" 
                justifyContent="center" 
                minHeight="60vh"
                textAlign="center"
                p={4}
            >
                <CircularProgress size={60} style={{ marginBottom: '20px' }} />
                <Typography variant="h4" gutterBottom>
                    Downloading File...
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    File ID: {fileId}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginTop: '10px' }}>
                    Debug: {debugInfo}
                </Typography>
            </Box>
        );
    }

    if (success) {
        return (
            <Box 
                display="flex" 
                flexDirection="column" 
                alignItems="center" 
                justifyContent="center" 
                minHeight="60vh"
                textAlign="center"
                p={4}
            >
                <Typography variant="h4" gutterBottom color="primary">
                    Download Started Successfully!
                </Typography>
                <Typography variant="body1" gutterBottom>
                    Your file download has been initiated. Check your downloads folder.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    File: {fileId}
                </Typography>
                <Button 
                    variant="contained" 
                    onClick={handleGoHome}
                    style={{ marginTop: '20px' }}
                >
                    Go to Dashboard
                </Button>
            </Box>
        );
    }

    if (error) {
        return (
            <Box 
                display="flex" 
                flexDirection="column" 
                alignItems="center" 
                justifyContent="center" 
                minHeight="60vh"
                textAlign="center"
                p={4}
            >
                <Alert severity="error" style={{ marginBottom: '20px', maxWidth: '600px' }}>
                    <Typography variant="h6">Download Error</Typography>
                    <Typography variant="body2">{error}</Typography>
                    <Typography variant="body2" style={{ marginTop: '10px' }}>
                        File ID: {fileId}
                    </Typography>
                    <Typography variant="body2" style={{ marginTop: '5px', fontSize: '12px' }}>
                        Debug: {debugInfo}
                    </Typography>
                </Alert>
                <Box>
                    <Button 
                        variant="contained" 
                        onClick={handleRetry}
                        style={{ marginRight: '10px' }}
                    >
                        Try Again
                    </Button>
                    <Button 
                        variant="outlined" 
                        onClick={handleGoHome}
                    >
                        Go to Dashboard
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            minHeight="60vh"
            textAlign="center"
            p={4}
        >
            <Typography variant="h4" gutterBottom>
                Preparing Download...
            </Typography>
            <Typography variant="body2" color="textSecondary">
                File ID: {fileId}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: '10px' }}>
                Debug: {debugInfo}
            </Typography>
            
            <Button 
                variant="outlined" 
                onClick={handleTestDownload}
                style={{ marginTop: '20px' }}
            >
                Test Download Manually
            </Button>
        </Box>
    );
};

export default EmailDownloadPage;
